import type {
  GetContextMenuItemsParams,
  GetMainMenuItemsParams,
  GridApi,
  GridOptions,
  IMenuActionParams,
  MenuItemDef,
} from 'ag-grid-community';
import { v4 as uuid } from 'uuid';
import { useMixpanel } from '../../../contexts';
import { MixpanelEvent } from '../../../tokens';
import { logger } from '../../../utils';
import { createExportFileName } from '../../../utils/fileExport';
import type { ExportOptions } from '../types';

import { cloneDeep, isFunction } from 'lodash-es';
import { useCallbackRef } from '../../../hooks';
import { useCurrentDocument } from '../../../hooks/useCurrentDocument';
import { IconName } from '../../Icons/IconNames';
import { getCustomColDefProperties } from '../columns/baseColumn';
import { columnTypes } from '../columns/index';
import type { Column, ColumnDynamics } from '../columns/types';
import { useGetDefaultContextMenuItems } from '../contextMenu';
import { isColDef } from '../types';
import type { ColumnDynamicsSpec } from '../useColumnDynamics/ColumnDynamics';
import { getColumnDynamicsState } from '../useColumnDynamics/ColumnDynamics';
import {
  addColumnChooserToMainMenu,
  getAllNodesInGroup,
  isGridApiReady,
  removeUnnecessarySeparators,
  safeGridApi,
} from '../utils';
import type { UseBlotterTable, UseBlotterTableProps } from './types';

export interface UseBlotterTableMenusParams<R> {
  gridApi: GridApi<R> | undefined;
  getExtraMainMenuItems?: GridOptions['getMainMenuItems'];
  getContextMenuItems?: GridOptions['getContextMenuItems'];

  columnDynamicsParams?: UseBlotterTableProps<R>['columnDynamicsParams'];

  exportOptions?: {
    csvOptions: {
      exportFunc: UseBlotterTable<unknown>['exportDataAsCSV'];
      params?: ExportOptions['csvParams'];
    };
    excelOptions: {
      exportFunc: UseBlotterTable<unknown>['exportDataAsExcel'];
      params?: ExportOptions['excelParams'];
    };
    baseParams?: ExportOptions['baseParams'];
    name: string;
    tabLabel?: string;
  };
}

export const useBlotterTableMenus = <R>({
  gridApi,
  getExtraMainMenuItems,
  getContextMenuItems,
  exportOptions,
  columnDynamicsParams,
}: UseBlotterTableMenusParams<R>) => {
  // Default context menu items
  const mixpanel = useMixpanel();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const currentDocument = useCurrentDocument();

  // This function intercepts the getting of context menu items (called on context menu open) and performs some uniform logic
  // Keeping it stable to lessen the amount of possible reasons for gridOptions to update. This is just an event handler, too, so it can be stateless.
  const getContextMenuItemsResolved = useCallbackRef((params: GetContextMenuItemsParams) => {
    const rightClickedNode = params.node;
    let isClickSelectionEnabled = true;
    const rowSelectionOptions = params.api.getGridOption('rowSelection');
    if (typeof rowSelectionOptions !== 'string' && typeof rowSelectionOptions?.enableClickSelection === 'boolean') {
      // [DEAL-5219] If click selection is disabled, we do not want to trigger the internal selection logic below.
      isClickSelectionEnabled = rowSelectionOptions.enableClickSelection;
    }

    if (rightClickedNode == null) {
      // Shouldn't happen
      logger.warn('No right clicked node found when attempting to build the BlotterTable AgGrid Context Menu');
      return [];
    }
    const selectedNodes = params.api.getSelectedNodes().filter(node => node.displayed);
    const isRightClickedNodeInSelection = selectedNodes.some(node => node.id === rightClickedNode.id);

    if (!isRightClickedNodeInSelection) {
      params.api.deselectAll();
    }

    if (rightClickedNode.group && isClickSelectionEnabled) {
      // If we have right clicked on a group row, we make sure to select the group row itself as well as all of its children before proceeding
      const nodes = [rightClickedNode, ...getAllNodesInGroup(rightClickedNode)];
      params.api.setNodesSelected({ nodes, newValue: true, source: 'rowClicked' });
    } else if (!rightClickedNode.rowPinned && isClickSelectionEnabled) {
      // Case of right-clicking on a normal row (also making sure its not pinned, aggrid doesn't like that), we select it
      rightClickedNode.setSelected(true, false);
    }
    const items = getContextMenuItems?.(params) ?? getDefaultContextMenuItems(params);

    if (exportOptions) {
      const { csvOptions, excelOptions, baseParams, tabLabel, name } = exportOptions;
      items.push('separator');
      const exportSubMenu: MenuItemDef = {
        name: 'Export',
        icon: `<i class="ag-icon ${IconName.DocumentDownload}"/>`,
        subMenu: [
          {
            name: `Export CSV`,
            icon: `<i class="ag-icon ${IconName.DocumentDownload}"/>`,
            action: () => {
              mixpanel.track(MixpanelEvent.ExportRows);
              csvOptions!.exportFunc({
                fileName: createExportFileName({ name: name || 'Export', tabLabel, mode: 'CSV' }),
                ...(isFunction(baseParams) ? baseParams(safeGridApi(gridApi)) : baseParams),
                ...csvOptions!.params,
              });
            },
          },
          {
            name: `Export Excel`,
            icon: `<i class="ag-icon ${IconName.DocumentDownload}"/>`,
            action: () => {
              mixpanel.track(MixpanelEvent.ExportRows);
              excelOptions!.exportFunc({
                fileName: createExportFileName({ name: name || 'Export', tabLabel, mode: 'Excel' }),
                ...(isFunction(baseParams) ? baseParams(safeGridApi(gridApi)) : baseParams),
                ...excelOptions!.params,
              });
            },
          },
        ],
      };
      items.push(exportSubMenu);
    }

    return removeUnnecessarySeparators(items);
  });

  function replaceItemInMenu(
    menuItems: Array<string | MenuItemDef<unknown, any>>,
    item: string,
    replacement: MenuItemDef
  ) {
    const index = menuItems.findIndex(menuItem => menuItem === item);
    if (index !== -1) {
      menuItems[index] = replacement;
    }
  }

  // Main menu
  // Made stable to avoid another reason for gridOptions to update. It is also just an event handler, so having it be stateless
  // and read its dependencies only when it is called is okay.
  const getMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    const extras = getExtraMainMenuItems ? getExtraMainMenuItems(params) : [];
    if (extras.length > 0) {
      extras.push('separator');
    }
    const defaultItems: Array<string | MenuItemDef<unknown, any>> = params.defaultItems.filter(
      item => item !== 'rowUnGroup'
    );

    addColumnChooserToMainMenu(defaultItems, params.api, currentDocument);

    // Add Reset Row Grouping option after Reset Columns (only if dynamic grouping is enabled)
    const resetColumnsIndex = defaultItems.findIndex(item =>
      typeof item === 'string' ? item === 'resetColumns' : item.name === 'Reset Columns'
    );

    if (resetColumnsIndex !== -1) {
      // Check if there are any columns with enableRowGroup: true
      const allColumns = params.api.getColumns() ?? [];
      const hasGroupableColumns = allColumns.some(col => col.getColDef().enableRowGroup === true);

      if (hasGroupableColumns) {
        const resetGroupingItem: MenuItemDef = {
          name: 'Reset Row Grouping',
          action: (params: IMenuActionParams) => {
            if (!isGridApiReady(params.api)) {
              return;
            }

            const initialRowGroupColumns = allColumns
              ?.filter(col => col.getColDef().initialRowGroup === true)
              .toSorted((a, b) => (a.getColDef().initialRowGroupIndex ?? 0) - (b.getColDef().initialRowGroupIndex ?? 0))
              .map(col => col.getColId());
            params.api.setRowGroupColumns(initialRowGroupColumns);
          },
        };
        defaultItems.splice(resetColumnsIndex + 1, 0, resetGroupingItem);
      }
    }

    replaceItemInMenu(defaultItems, 'expandAll', {
      name: 'Expand All Row Groups',
      icon: `<i class="ag-icon ${IconName.ListExpand}"/>`,
      action: () => params.api.expandAll(),
    });

    replaceItemInMenu(defaultItems, 'contractAll', {
      name: 'Collapse All Row Groups',
      icon: `<i class="ag-icon ${IconName.ListCollapse}"/>`,
      action: () => params.api.collapseAll(),
    });

    if (columnDynamicsParams) {
      defaultItems.push(...getColumnDynamicsMainMenuItems(columnDynamicsParams.columnDynamicsSpecs, params));
    }

    return removeUnnecessarySeparators(extras.concat(defaultItems));
  });

  return { getMainMenuItems, getContextMenuItems: getContextMenuItemsResolved };
};

/** When cloning a column with dynamics, use this function to correctly get the new cloned dynamic descriptor */
export function cloneColumnDynamics<StateType>(
  sourceDynamics: ColumnDynamics<StateType>,
  colID: string
): ColumnDynamics<StateType> {
  const sourceSettings = getColumnDynamicsState(sourceDynamics);
  return {
    colID,
    spec: sourceDynamics.spec,
    removable: true,
    initialState: cloneDeep(sourceSettings),
    state: cloneDeep(sourceSettings),
  };
}

/** For columns that support dynamics, this function collects the MenuItemDefs necessary for the user to be able to interact with the mechanism */
function getColumnDynamicsMainMenuItems(specs: ColumnDynamicsSpec[], params: GetMainMenuItemsParams): MenuItemDef[] {
  const addItem: MenuItemDef = {
    name: 'Add Column',
    subMenu: specs.map(spec => ({
      name: spec.label,
      action: () => {
        const initialSettings = spec.getInitialSettings();
        const dynamics: ColumnDynamics = {
          colID: uuid(),
          spec: spec.type,
          removable: true,
          initialState: initialSettings,
          state: initialSettings,
        };
        addColumn(params.api, spec.getColumn(dynamics));
        if (spec.openSettingsOnCreation) {
          spec.openSettings?.(getColumnDynamicsState(dynamics), getSaveColumnSettingsFn(params.api, dynamics, spec));
        }
      },
    })),
  };

  const items = [addItem];

  const clickedColumnCustomProperties = getCustomColDefProperties(params.column.getColDef());
  const dynamics = clickedColumnCustomProperties?.dynamics;
  const spec = specs.find(spec => spec.type === dynamics?.spec);
  if (spec && dynamics) {
    items.push({
      name: 'Clone Column',
      action: () => {
        const clonedDynamics = cloneColumnDynamics(dynamics, uuid());
        addColumn(params.api, spec.getColumn(clonedDynamics));
      },
    });
  }

  if (spec?.openSettings && dynamics) {
    // we are a column with dynamics, so we can edit and remove ourselves (if removable is true)
    items.push({
      name: 'Edit Column',
      action: () =>
        spec.openSettings?.(getColumnDynamicsState(dynamics), getSaveColumnSettingsFn(params.api, dynamics, spec)),
    });
  }

  if (dynamics?.removable) {
    items.push({
      name: 'Remove Column',
      action: () => removeColumn(params.api, params.column.getColId()),
    });
  }

  return items;
}

/** Applies the provided dynamics onto the grid's column defs */
function getSaveColumnSettingsFn(unsafeApi: GridApi, dynamics: ColumnDynamics | undefined, spec: ColumnDynamicsSpec) {
  return (newSettings: ColumnDynamics['state']) => {
    if (!dynamics) {
      return;
    }

    const api = safeGridApi(unsafeApi);
    if (!api) {
      return;
    }

    const columnDefs = api.getColumnDefs();
    if (!columnDefs) {
      return;
    }

    api.setGridOption(
      'columnDefs',
      columnDefs.map(colDef => {
        if (isColDef(colDef) && colDef.colId === dynamics.colID) {
          const newColumn = spec.getColumn({ ...dynamics, state: newSettings });
          return columnTypes[newColumn.type](newColumn);
        }

        return colDef;
      })
    );
  };
}

/** Adds a new Column to the grid's columnDefs */
function addColumn(unsafeApi: GridApi, column: Column) {
  const api = safeGridApi(unsafeApi);
  if (!api) {
    return;
  }

  const currentColumnDefs = api.getColumnDefs();
  if (!currentColumnDefs) {
    return;
  }

  api.setGridOption('columnDefs', [...currentColumnDefs, columnTypes[column.type](column)]);
}

/** Removes a column from the grid's  columnDefs */
function removeColumn(unsafeApi: GridApi, colIDToRemove: string) {
  const api = safeGridApi(unsafeApi);
  if (!api) {
    return;
  }

  const currentColumnDefs = api.getColumnDefs();
  if (!currentColumnDefs) {
    return;
  }

  api.setGridOption(
    'columnDefs',
    currentColumnDefs.filter(colDef => {
      if (isColDef(colDef)) {
        return colDef.colId !== colIDToRemove;
      }

      return true; // coldef groups
    })
  );
}
